import React, { useState, useEffect, useCallback } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Sidebar, MobileHeader } from '../components/shared/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wallet, TrendingUp, TrendingDown, DollarSign, Plus, Minus, 
  CreditCard, Bitcoin, Building2, Smartphone, PiggyBank,
  ArrowUpRight, ArrowDownRight, Settings, History, BarChart3,
  X, Check, Coins, Crown, Zap, Shield, Target,
  Edit2, Trash2, Filter, Download, AlertTriangle, Eye, Users,
  ChevronLeft, ChevronRight, Clock, Search, Send
} from 'lucide-react';
import { formatCurrency, formatDateTime } from '../lib/utils';

// Animated Counter
function AnimatedNumber({ value, prefix = '' }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const end = parseFloat(value) || 0;
    const duration = 1500;
    const start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      setDisplay(end * (1 - Math.pow(1 - progress, 3)));
      if (progress < 1) requestAnimationFrame(animate);
    };
    animate();
  }, [value]);
  return <span className="font-mono tabular-nums">{prefix}{Math.floor(display).toLocaleString()}</span>;
}

// Wallet Icon mapper
const getWalletIcon = (name) => {
  const lowerName = (name || '').toLowerCase();
  if (lowerName.includes('cash app')) return Smartphone;
  if (lowerName.includes('venmo')) return Smartphone;
  if (lowerName.includes('paypal')) return CreditCard;
  if (lowerName.includes('chime')) return Building2;
  if (lowerName.includes('crypto') || lowerName.includes('bitcoin')) return Bitcoin;
  if (lowerName.includes('bank')) return Building2;
  return Wallet;
};

// Wallet Color mapper
const getWalletColor = (name) => {
  const lowerName = (name || '').toLowerCase();
  if (lowerName.includes('cash app')) return '#00FF88';
  if (lowerName.includes('venmo')) return '#008CFF';
  if (lowerName.includes('paypal')) return '#003087';
  if (lowerName.includes('chime')) return '#00A36C';
  if (lowerName.includes('crypto') || lowerName.includes('bitcoin')) return '#FFD700';
  if (lowerName.includes('bank')) return '#0088FF';
  return '#9945FF';
};

export default function WalletPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('wallets');
  
  // Wallets data
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [walletHistory, setWalletHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Filter states
  const [periodFilter, setPeriodFilter] = useState('all');
  const [historyPage, setHistoryPage] = useState(1);
  const historyPerPage = 15;
  
  // Players for cash out
  const [players, setPlayers] = useState([]);
  const [playerSearch, setPlayerSearch] = useState('');
  
  // Modal states
  const [showAddWallet, setShowAddWallet] = useState(false);
  const [showCashToPlayer, setShowCashToPlayer] = useState(false);
  const [editingWallet, setEditingWallet] = useState(null);
  
  // Forms
  const [walletForm, setWalletForm] = useState({
    wallet_name: '',
    tag_name: '',
    receive_limit: '',
    auto_approve: false
  });
  const [cashToPlayerForm, setCashToPlayerForm] = useState({
    wallet_id: '',
    player_name: '',
    player_id: '',
    amount: ''
  });

  // Admin deposit form
  const [depositForm, setDepositForm] = useState({ player_name: '', player_id: '', game_name: '', deposit_amount: '', loaded_amount: '', payment_source: '' });
  const [depositPlayerSearch, setDepositPlayerSearch] = useState('');
  const [depositSubmitting, setDepositSubmitting] = useState(false);

  const depositFilteredPlayers = players.filter(p =>
    p.name?.toLowerCase().includes(depositPlayerSearch.toLowerCase())
  ).slice(0, 8);

  const handleAdminDeposit = async (e) => {
    e.preventDefault();
    if (!depositForm.player_id) {
      toast.error('Select a valid player');
      return;
    }
    setDepositSubmitting(true);
    try {
      await axios.post('/transactions', {
        player_id: depositForm.player_id,
        player_name: depositForm.player_name,
        game_name: depositForm.game_name || 'N/A',
        transaction_type: 'deposit',
        deposit_amount: parseFloat(depositForm.deposit_amount) || 0,
        loaded_amount: parseFloat(depositForm.loaded_amount) || 0,
        payment_source: depositForm.payment_source || 'N/A',
      });
      toast.success(`Deposit of ${formatCurrency(parseFloat(depositForm.deposit_amount))} submitted for ${depositForm.player_name}`);
      setDepositForm({ player_name: '', player_id: '', game_name: '', deposit_amount: '', loaded_amount: '', payment_source: '' });
      fetchWallets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit deposit');
    }
    setDepositSubmitting(false);
  };

  // Calculate totals
  const totalBalance = wallets.reduce((sum, w) => sum + (w.balance || 0), 0);
  const totalIn = wallets.reduce((sum, w) => sum + (w.total_in || 0), 0);
  const totalOut = wallets.reduce((sum, w) => sum + (w.total_out || 0), 0);
  const walletsOverLimit = wallets.filter(w => w.over_limit);

  const fetchWallets = useCallback(async () => {
    try {
      const response = await axios.get('/wallets');
      setWallets(response.data.wallets || []);
    } catch (error) {
      toast.error('Failed to load wallets');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPlayers = useCallback(async () => {
    try {
      const response = await axios.get('/players');
      setPlayers(Array.isArray(response.data) ? response.data : response.data.players || []);
    } catch (error) {
      console.error('Failed to load players');
    }
  }, []);

  const fetchWalletHistory = useCallback(async (walletId, period = null) => {
    try {
      setHistoryLoading(true);
      const params = new URLSearchParams();
      if (period && period !== 'all') params.append('period', period);
      
      const response = await axios.get(`/wallets/${walletId}/history?${params.toString()}`);
      setWalletHistory(response.data.transactions || []);
      setHistoryPage(1);
    } catch (error) {
      toast.error('Failed to load wallet history');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const [showWalletOnWithdraw, setShowWalletOnWithdraw] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const fetchAdminSettings = useCallback(async () => {
    try {
      const res = await axios.get('/admin/settings');
      setShowWalletOnWithdraw(res.data.show_wallet_on_withdraw || false);
    } catch (e) {}
  }, []);

  useEffect(() => {
    fetchWallets();
    fetchPlayers();
    fetchAdminSettings();
  }, [fetchWallets, fetchPlayers, fetchAdminSettings]);

  useAutoRefresh(fetchWallets, 15000);

  // Fetch history when wallet selected or period changes
  useEffect(() => {
    if (selectedWallet) {
      fetchWalletHistory(selectedWallet.id, periodFilter);
    }
  }, [selectedWallet, periodFilter, fetchWalletHistory]);

  const handleCreateWallet = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        wallet_name: walletForm.wallet_name,
        tag_name: walletForm.tag_name || null,
        receive_limit: parseFloat(walletForm.receive_limit) || 0,
        auto_approve: walletForm.auto_approve
      };
      
      if (editingWallet) {
        await axios.put(`/wallets/${editingWallet.id}`, payload);
        toast.success('Wallet updated!');
      } else {
        await axios.post('/wallets', payload);
        toast.success('Wallet created!');
      }
      
      setShowAddWallet(false);
      setEditingWallet(null);
      setWalletForm({ wallet_name: '', tag_name: '', receive_limit: '', auto_approve: false });
      fetchWallets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const toggleShowWalletOnWithdraw = async () => {
    setSettingsLoading(true);
    try {
      const newVal = !showWalletOnWithdraw;
      await axios.put('/admin/settings', { show_wallet_on_withdraw: newVal });
      setShowWalletOnWithdraw(newVal);
      toast.success(newVal ? 'Wallet shown on withdraw forms' : 'Wallet hidden from withdraw forms');
    } catch (e) {
      toast.error('Failed to update setting');
    }
    setSettingsLoading(false);
  };

  const handleDeleteWallet = async (walletId) => {
    setDeleteConfirm(walletId);
  };

  const confirmDeleteWallet = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`/wallets/${deleteConfirm}`);
      toast.success('Wallet deleted');
      fetchWallets();
      if (selectedWallet?.id === deleteConfirm) {
        setSelectedWallet(null);
      }
    } catch (error) {
      toast.error('Failed to delete wallet');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleCashToPlayer = async (e) => {
    e.preventDefault();
    try {
      const wallet = wallets.find(w => w.id === cashToPlayerForm.wallet_id);
      await axios.post('/cash-to-player', {
        ...cashToPlayerForm,
        amount: parseFloat(cashToPlayerForm.amount),
        wallet_name: wallet ? `${wallet.wallet_name}${wallet.tag_name ? ' - ' + wallet.tag_name : ''}` : 'Unknown'
      });
      toast.success(`Cash sent to ${cashToPlayerForm.player_name}!`);
      setShowCashToPlayer(false);
      setCashToPlayerForm({ wallet_id: '', player_name: '', player_id: '', amount: '' });
      fetchWallets();
      if (selectedWallet) {
        fetchWalletHistory(selectedWallet.id, periodFilter);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send cash');
    }
  };

  const toggleWalletVisibility = async (wallet) => {
    try {
      await axios.put(`/wallets/${wallet.id}`, { is_hidden: !wallet.is_hidden });
      toast.success(wallet.is_hidden ? 'Wallet is now visible' : 'Wallet hidden from deposit forms');
      fetchWallets();
    } catch { toast.error('Failed to update'); }
  };

  const openEditWallet = (wallet) => {
    setEditingWallet(wallet);
    setWalletForm({
      wallet_name: wallet.wallet_name,
      tag_name: wallet.tag_name || '',
      receive_limit: wallet.receive_limit?.toString() || '',
      auto_approve: wallet.auto_approve || false
    });
    setShowAddWallet(true);
  };

  const openWalletDetail = (wallet) => {
    setSelectedWallet(wallet);
    setPeriodFilter('all');
    setActiveTab('detail');
  };

  // Pagination for history
  const paginatedHistory = walletHistory.slice(
    (historyPage - 1) * historyPerPage,
    historyPage * historyPerPage
  );
  const totalHistoryPages = Math.ceil(walletHistory.length / historyPerPage);

  // Filter players for search
  const filteredPlayers = players.filter(p => 
    p.name?.toLowerCase().includes(playerSearch.toLowerCase())
  ).slice(0, 8);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#080810]">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }} className="relative">
          <div className="w-20 h-20 rounded-full border-4 border-[#FFD700]/30 border-t-[#FFD700]" />
          <Coins className="absolute inset-0 m-auto w-8 h-8 text-[#FFD700]" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080810]" data-testid="wallet-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />

      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#FFD700]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-[#9945FF]/5 rounded-full blur-[150px]" />
      </div>

      <main className="lg:ml-72 min-h-screen relative z-10 p-4 md:p-6 lg:p-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-4">
            <motion.div
              animate={{ 
                boxShadow: ['0 0 20px #FFD700', '0 0 40px #FFD700', '0 0 20px #FFD700'],
              }}
              transition={{ repeat: Infinity, duration: 3 }}
              className="p-3 rounded-xl bg-gradient-to-br from-[#FFD700]/20 to-[#FF6B00]/20 border border-[#FFD700]/30"
            >
              <Coins className="w-8 h-8 text-[#FFD700]" />
            </motion.div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-white">TREASURY VAULTS</h1>
              <p className="text-gray-400">Manage your payment wallets</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 items-center">
            {/* Show Wallet on Withdraw Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl border" 
              style={{
                background: showWalletOnWithdraw ? 'rgba(0,255,136,0.08)' : 'rgba(255,255,255,0.03)',
                borderColor: showWalletOnWithdraw ? 'rgba(0,255,136,0.3)' : 'rgba(255,255,255,0.1)'
              }}
              data-testid="show-wallet-withdraw-toggle-container"
            >
              <Wallet className="w-4 h-4" style={{ color: showWalletOnWithdraw ? '#00FF88' : '#666' }} />
              <span className="text-xs font-bold text-gray-300 whitespace-nowrap">Wallet on Withdraw</span>
              <button
                type="button"
                onClick={toggleShowWalletOnWithdraw}
                disabled={settingsLoading}
                className="relative w-10 h-5 rounded-full transition-colors"
                style={{ background: showWalletOnWithdraw ? '#00FF88' : '#333' }}
                data-testid="show-wallet-withdraw-toggle"
              >
                <div className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all" style={{ left: showWalletOnWithdraw ? '22px' : '2px' }} />
              </button>
              <span className={`text-xs font-bold ${showWalletOnWithdraw ? 'text-[#00FF88]' : 'text-gray-500'}`}>
                {showWalletOnWithdraw ? 'ON' : 'OFF'}
              </span>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => { setEditingWallet(null); setWalletForm({ wallet_name: '', tag_name: '', receive_limit: '', auto_approve: false }); setShowAddWallet(true); }}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#00FF88] to-[#00F5FF] text-black font-bold flex items-center gap-2"
              data-testid="add-wallet-btn"
            >
              <Plus className="w-4 h-4" /> ADD WALLET
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCashToPlayer(true)}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#FF3366] to-[#FF6B00] text-white font-bold flex items-center gap-2"
              data-testid="cash-to-player-btn"
            >
              <Send className="w-4 h-4" /> CASH TO PLAYER
            </motion.button>
          </div>
        </motion.div>

        {/* Warning Banner if any wallets over limit */}
        <AnimatePresence>
          {walletsOverLimit.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6"
            >
              <Card className="bg-gradient-to-r from-[#FF3366]/20 to-[#FF6B00]/20 border border-[#FF3366]/50 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#FF3366]/20">
                    <AlertTriangle className="w-6 h-6 text-[#FF3366] animate-pulse" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-bold">LIMIT WARNING!</p>
                    <p className="text-gray-300 text-sm">
                      {walletsOverLimit.length} wallet(s) at or over receive limit: {' '}
                      {walletsOverLimit.map(w => `${w.wallet_name}${w.tag_name ? ' - ' + w.tag_name : ''}`).join(', ')}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="border-[#FF3366]/50 text-[#FF3366] hover:bg-[#FF3366]/20"
                    onClick={() => openWalletDetail(walletsOverLimit[0])}
                  >
                    View
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats Summary */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <Card className="relative overflow-hidden bg-gradient-to-br from-[#1a1a2e] via-[#0d0d15] to-[#1a1a2e] border-0 p-6 md:p-8">
            <div className="absolute inset-0 hex-pattern opacity-20" />
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent" />
            
            <div className="relative grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center md:text-left">
                <p className="text-sm text-gray-400 flex items-center gap-2 mb-2 justify-center md:justify-start">
                  <Crown className="w-4 h-4 text-[#FFD700]" /> TOTAL BALANCE
                </p>
                <motion.p 
                  className="text-3xl md:text-4xl font-black text-[#FFD700]"
                  style={{ textShadow: '0 0 20px rgba(255,215,0,0.3)' }}
                  data-testid="total-balance"
                >
                  <AnimatedNumber value={totalBalance} prefix="$" />
                </motion.p>
              </div>
              
              <div className="text-center md:text-left">
                <p className="text-sm text-gray-400 flex items-center gap-2 mb-2 justify-center md:justify-start">
                  <TrendingUp className="w-4 h-4 text-[#00FF88]" /> TOTAL IN
                </p>
                <p className="text-2xl md:text-3xl font-bold text-[#00FF88]">
                  <AnimatedNumber value={totalIn} prefix="$" />
                </p>
              </div>
              
              <div className="text-center md:text-left">
                <p className="text-sm text-gray-400 flex items-center gap-2 mb-2 justify-center md:justify-start">
                  <TrendingDown className="w-4 h-4 text-[#FF3366]" /> TOTAL OUT
                </p>
                <p className="text-2xl md:text-3xl font-bold text-[#FF3366]">
                  <AnimatedNumber value={totalOut} prefix="$" />
                </p>
              </div>
              
              <div className="text-center md:text-left">
                <p className="text-sm text-gray-400 flex items-center gap-2 mb-2 justify-center md:justify-start">
                  <Wallet className="w-4 h-4 text-[#9945FF]" /> WALLETS
                </p>
                <p className="text-2xl md:text-3xl font-bold text-[#9945FF]">
                  {wallets.length}
                </p>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-lg grid-cols-3 bg-[#0d0d15] p-1 rounded-xl border border-white/5">
            <TabsTrigger 
              value="wallets" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#9945FF] data-[state=active]:to-[#00F5FF] data-[state=active]:text-black rounded-lg font-bold"
            >
              <Wallet className="w-4 h-4 mr-2" /> All Wallets
            </TabsTrigger>
            <TabsTrigger 
              value="deposit" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#00FF88] data-[state=active]:to-[#00F5FF] data-[state=active]:text-black rounded-lg font-bold"
              data-testid="deposit-tab"
            >
              <Plus className="w-4 h-4 mr-2" /> Deposit
            </TabsTrigger>
            <TabsTrigger 
              value="detail" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#9945FF] data-[state=active]:to-[#00F5FF] data-[state=active]:text-black rounded-lg font-bold"
              disabled={!selectedWallet}
            >
              <Eye className="w-4 h-4 mr-2" /> {selectedWallet ? 'Wallet Detail' : 'Select Wallet'}
            </TabsTrigger>
          </TabsList>

          {/* All Wallets Tab */}
          <TabsContent value="wallets" className="space-y-4">
            {wallets.length === 0 ? (
              <Card className="bg-[#0d0d15] border-0 p-12 text-center">
                <Wallet className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl font-bold text-white mb-2">No Wallets Yet</h3>
                <p className="text-gray-400 mb-6">Add your first wallet to start tracking payments</p>
                <Button
                  onClick={() => setShowAddWallet(true)}
                  className="bg-gradient-to-r from-[#00FF88] to-[#00F5FF] text-black font-bold"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add First Wallet
                </Button>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {wallets.map((wallet, idx) => {
                  const IconComponent = getWalletIcon(wallet.wallet_name);
                  const color = getWalletColor(wallet.wallet_name);
                  const limitPercent = wallet.receive_limit > 0 
                    ? Math.min((wallet.balance / wallet.receive_limit) * 100, 100)
                    : 0;
                  
                  return (
                    <motion.div
                      key={wallet.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      whileHover={{ scale: 1.02, y: -5 }}
                    >
                      <Card 
                        className={`bg-[#0d0d15] border-0 p-5 relative overflow-hidden cursor-pointer group ${
                          wallet.over_limit ? 'ring-2 ring-[#FF3366]/50' : ''
                        }`}
                        onClick={() => openWalletDetail(wallet)}
                        data-testid={`wallet-card-${wallet.id}`}
                      >
                        <div 
                          className="absolute top-0 left-0 w-full h-[2px]" 
                          style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} 
                        />
                        
                        {/* Show/Hide header */}
                        <div className={`-mx-5 -mt-5 mb-3 px-3 py-1.5 flex items-center justify-between ${
                          wallet.is_hidden ? 'bg-[#FF3366]/10' : 'bg-[#00FF88]/10'
                        }`} onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleWalletVisibility(wallet); }}
                            className="flex items-center gap-1.5"
                            data-testid={`toggle-visibility-${wallet.id}`}
                          >
                            <div className="relative w-9 h-5 rounded-full transition-colors" style={{ background: wallet.is_hidden ? '#FF3366' : '#00FF88' }}>
                              <div className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all" style={{ left: wallet.is_hidden ? '2px' : '18px' }} />
                            </div>
                            <span className={`text-xs font-bold uppercase tracking-wider ${wallet.is_hidden ? 'text-[#FF3366]' : 'text-[#00FF88]'}`}>
                              {wallet.is_hidden ? 'OFF' : 'ON'}
                            </span>
                          </button>
                        </div>
                        
                        {/* Over limit warning badge */}
                        {wallet.over_limit && (
                          <div className="absolute top-2 right-2">
                            <Badge className="bg-[#FF3366]/20 text-[#FF3366] border-[#FF3366]/30 animate-pulse">
                              <AlertTriangle className="w-3 h-3 mr-1" /> LIMIT!
                            </Badge>
                          </div>
                        )}
                        {/* Auto approve badge */}
                        {wallet.auto_approve && !wallet.over_limit && (
                          <div className="absolute top-2 right-2">
                            <Badge className="bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/30">
                              <Zap className="w-3 h-3 mr-1" /> AUTO
                            </Badge>
                          </div>
                        )}
                        {wallet.auto_approve && wallet.over_limit && (
                          <div className="absolute top-9 right-2">
                            <Badge className="bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/30">
                              <Zap className="w-3 h-3 mr-1" /> AUTO
                            </Badge>
                          </div>
                        )}
                        
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2.5 rounded-xl" style={{ background: `${color}20` }}>
                            <IconComponent className="w-6 h-6" style={{ color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-white text-lg truncate">{wallet.wallet_name}</h3>
                            {wallet.tag_name && (
                              <p className="text-sm text-gray-400 truncate">@{wallet.tag_name}</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Balance */}
                        <div className="mb-4">
                          <p className="text-xs text-gray-500 mb-1">Balance</p>
                          <p className="text-2xl font-black text-white font-mono">
                            {formatCurrency(wallet.balance || 0)}
                          </p>
                        </div>
                        
                        {/* In/Out */}
                        <div className="flex items-center gap-4 mb-4 text-sm">
                          <div className="flex items-center gap-1">
                            <ArrowUpRight className="w-4 h-4 text-[#00FF88]" />
                            <span className="text-[#00FF88] font-mono">{formatCurrency(wallet.total_in || 0)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <ArrowDownRight className="w-4 h-4 text-[#FF3366]" />
                            <span className="text-[#FF3366] font-mono">{formatCurrency(wallet.total_out || 0)}</span>
                          </div>
                        </div>
                        
                        {/* Limit Progress */}
                        {wallet.receive_limit > 0 && (
                          <div>
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                              <span>Receive Limit</span>
                              <span>{formatCurrency(wallet.balance || 0)} / {formatCurrency(wallet.receive_limit)}</span>
                            </div>
                            <div className="h-2 bg-[#1a1a2e] rounded-full overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${limitPercent}%` }}
                                transition={{ duration: 1 }}
                                className={`h-full rounded-full ${
                                  limitPercent >= 100 ? 'bg-[#FF3366]' : 
                                  limitPercent >= 80 ? 'bg-[#FFD700]' : 'bg-[#00FF88]'
                                }`}
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* Action buttons (show on hover) */}
                        <div className="absolute bottom-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 bg-[#1a1a2e] hover:bg-[#00F5FF]/20"
                            onClick={(e) => { e.stopPropagation(); openEditWallet(wallet); }}
                            data-testid={`edit-wallet-${wallet.id}`}
                          >
                            <Edit2 className="w-4 h-4 text-[#00F5FF]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 bg-[#1a1a2e] hover:bg-[#FF3366]/20"
                            onClick={(e) => { e.stopPropagation(); handleDeleteWallet(wallet.id); }}
                            data-testid={`delete-wallet-${wallet.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-[#FF3366]" />
                          </Button>
                        </div>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Deposit Tab */}
          <TabsContent value="deposit" className="space-y-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="bg-[#0d0d15] border-0 overflow-hidden max-w-xl mx-auto">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00FF88] to-transparent" />
                
                <div className="p-5 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-[#00FF88]/15 border border-[#00FF88]/30">
                      <DollarSign className="w-6 h-6 text-[#00FF88]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">NEW DEPOSIT</h2>
                      <p className="text-sm text-gray-400">Submit a deposit transaction</p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleAdminDeposit} className="p-5 space-y-4">
                  {/* Player */}
                  <div>
                    <Label className="text-gray-300 text-sm">Player *</Label>
                    <div className="relative mt-1.5">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        value={depositForm.player_name}
                        onChange={(e) => {
                          setDepositForm({ ...depositForm, player_name: e.target.value, player_id: '' });
                          setDepositPlayerSearch(e.target.value);
                        }}
                        placeholder="Search player..."
                        className="pl-9 bg-[#1a1a2e] border-white/10 text-white h-11"
                        required
                        data-testid="admin-deposit-player"
                      />
                    </div>
                    {depositPlayerSearch && !depositForm.player_id && depositFilteredPlayers.length > 0 && (
                      <div className="mt-1 rounded-lg bg-[#1a1a2e] border border-white/10 max-h-36 overflow-y-auto">
                        {depositFilteredPlayers.map(p => (
                          <button key={p.id} type="button"
                            className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 flex items-center justify-between"
                            onClick={() => { setDepositForm({ ...depositForm, player_name: p.name, player_id: p.id }); setDepositPlayerSearch(''); }}>
                            <span className="font-medium">{p.name}</span>
                            {p.player_tag && <span className="text-gray-500 text-xs">{p.player_tag}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                    {depositForm.player_id && (
                      <p className="text-xs text-[#00FF88] mt-1 flex items-center gap-1"><Check className="w-3 h-3" /> Player selected</p>
                    )}
                  </div>

                  {/* Game & Deposit Amount side by side */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-gray-300 text-sm">Game Name</Label>
                      <Input
                        value={depositForm.game_name}
                        onChange={(e) => setDepositForm({ ...depositForm, game_name: e.target.value })}
                        placeholder="e.g., Slots, Poker..."
                        className="bg-[#1a1a2e] border-white/10 text-white mt-1.5 h-11"
                        data-testid="admin-deposit-game"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 text-sm">Deposit Amount *</Label>
                      <div className="relative mt-1.5">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <Input
                          type="number" step="0.01" min="0.01"
                          value={depositForm.deposit_amount}
                          onChange={(e) => setDepositForm({ ...depositForm, deposit_amount: e.target.value })}
                          placeholder="0.00"
                          className="pl-9 bg-[#1a1a2e] border-white/10 text-white font-mono h-11"
                          required
                          data-testid="admin-deposit-amount"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Loaded Amount */}
                  <div>
                    <Label className="text-gray-300 text-sm">Loaded Amount</Label>
                    <div className="relative mt-1.5">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        type="number" step="0.01" min="0"
                        value={depositForm.loaded_amount}
                        onChange={(e) => setDepositForm({ ...depositForm, loaded_amount: e.target.value })}
                        placeholder="0.00"
                        className="pl-9 bg-[#1a1a2e] border-white/10 text-white font-mono h-11"
                        data-testid="admin-deposit-loaded"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Amount loaded to player's game wallet</p>
                  </div>

                  {/* Wallet / Payment Source */}
                  <div>
                    <Label className="text-gray-300 text-sm">Payment Source (Wallet) *</Label>
                    <select
                      value={depositForm.payment_source}
                      onChange={(e) => setDepositForm({ ...depositForm, payment_source: e.target.value })}
                      className="w-full mt-1.5 rounded-lg px-3 py-2.5 bg-[#1a1a2e] border border-white/10 text-white h-11"
                      required
                      data-testid="admin-deposit-wallet"
                    >
                      <option value="">Select wallet...</option>
                      {wallets.filter(w => !w.is_hidden).map(w => (
                        <option key={w.id} value={`${w.wallet_name}${w.tag_name ? ' - ' + w.tag_name : ''}`}>
                          {w.wallet_name}{w.tag_name ? ` - ${w.tag_name}` : ''} ({formatCurrency(w.balance || 0)})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Preview */}
                  {depositForm.deposit_amount && depositForm.player_name && (
                    <div className="p-4 rounded-xl border border-[#00FF88]/20 bg-[#00FF88]/5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-400">Deposit for</p>
                          <p className="text-white font-bold">{depositForm.player_name}</p>
                        </div>
                        <p className="text-2xl font-black text-[#00FF88] font-mono">
                          {formatCurrency(parseFloat(depositForm.deposit_amount) || 0)}
                        </p>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={depositSubmitting || !depositForm.player_id || !depositForm.deposit_amount || !depositForm.payment_source}
                    className="w-full h-12 bg-gradient-to-r from-[#00FF88] to-[#00F5FF] text-black font-bold text-base"
                    data-testid="submit-admin-deposit"
                  >
                    {depositSubmitting ? (
                      <div className="w-5 h-5 rounded-full border-2 border-black/30 border-t-black animate-spin" />
                    ) : (
                      <><Plus className="w-5 h-5 mr-2" /> SUBMIT DEPOSIT</>
                    )}
                  </Button>
                </form>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Wallet Detail Tab */}
          <TabsContent value="detail" className="space-y-6">
            {selectedWallet ? (
              <>
                {/* Wallet Header Card */}
                <Card className="bg-[#0d0d15] border-0 p-6 relative overflow-hidden">
                  <div 
                    className="absolute top-0 left-0 w-full h-[2px]" 
                    style={{ background: `linear-gradient(90deg, transparent, ${getWalletColor(selectedWallet.wallet_name)}, transparent)` }}
                  />
                  
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setSelectedWallet(null); setActiveTab('wallets'); }}
                        className="hover:bg-white/10"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </Button>
                      
                      <div 
                        className="p-3 rounded-xl" 
                        style={{ background: `${getWalletColor(selectedWallet.wallet_name)}20` }}
                      >
                        {React.createElement(getWalletIcon(selectedWallet.wallet_name), {
                          className: "w-8 h-8",
                          style: { color: getWalletColor(selectedWallet.wallet_name) }
                        })}
                      </div>
                      
                      <div>
                        <h2 className="text-2xl font-bold text-white">
                          {selectedWallet.wallet_name}
                          {selectedWallet.tag_name && (
                            <span className="text-gray-400 ml-2">- {selectedWallet.tag_name}</span>
                          )}
                        </h2>
                        <div className="flex items-center gap-4 mt-1">
                          {selectedWallet.over_limit && (
                            <Badge className="bg-[#FF3366]/20 text-[#FF3366] border-[#FF3366]/30">
                              <AlertTriangle className="w-3 h-3 mr-1" /> Over Limit
                            </Badge>
                          )}
                          {selectedWallet.receive_limit > 0 && (
                            <p className="text-sm text-gray-400">
                              Limit: {formatCurrency(selectedWallet.receive_limit)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-gray-400">Current Balance</p>
                        <p className="text-3xl font-black text-[#FFD700] font-mono" data-testid="wallet-detail-balance">
                          {formatCurrency(selectedWallet.balance || 0)}
                        </p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={() => openEditWallet(selectedWallet)}
                          variant="outline"
                          className="border-[#00F5FF]/30 text-[#00F5FF] hover:bg-[#00F5FF]/10"
                        >
                          <Edit2 className="w-4 h-4 mr-2" /> Edit
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Stats Row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-white/5">
                    <div className="p-3 rounded-xl bg-[#00FF88]/10 border border-[#00FF88]/30 text-center">
                      <TrendingUp className="w-5 h-5 text-[#00FF88] mx-auto mb-1" />
                      <p className="text-xs text-gray-400">Total In</p>
                      <p className="text-lg font-bold font-mono text-[#00FF88]">
                        {formatCurrency(selectedWallet.total_in || 0)}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-[#FF3366]/10 border border-[#FF3366]/30 text-center">
                      <TrendingDown className="w-5 h-5 text-[#FF3366] mx-auto mb-1" />
                      <p className="text-xs text-gray-400">Total Out</p>
                      <p className="text-lg font-bold font-mono text-[#FF3366]">
                        {formatCurrency(selectedWallet.total_out || 0)}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-[#9945FF]/10 border border-[#9945FF]/30 text-center">
                      <History className="w-5 h-5 text-[#9945FF] mx-auto mb-1" />
                      <p className="text-xs text-gray-400">Transactions</p>
                      <p className="text-lg font-bold font-mono text-[#9945FF]">
                        {walletHistory.length}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-[#FFD700]/10 border border-[#FFD700]/30 text-center">
                      <Target className="w-5 h-5 text-[#FFD700] mx-auto mb-1" />
                      <p className="text-xs text-gray-400">Limit Used</p>
                      <p className="text-lg font-bold font-mono text-[#FFD700]">
                        {selectedWallet.receive_limit > 0 
                          ? `${Math.min(((selectedWallet.balance || 0) / selectedWallet.receive_limit) * 100, 100).toFixed(0)}%`
                          : 'N/A'
                        }
                      </p>
                    </div>
                  </div>
                </Card>
                
                {/* Transaction History */}
                <Card className="bg-[#0d0d15] border-0 overflow-hidden">
                  <div className="p-4 border-b border-white/5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <h3 className="font-bold text-white flex items-center gap-2">
                      <History className="w-5 h-5 text-[#9945FF]" /> TRANSACTION HISTORY
                    </h3>
                    
                    {/* Period Filter */}
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <Select value={periodFilter} onValueChange={setPeriodFilter}>
                        <SelectTrigger className="w-[140px] bg-[#1a1a2e] border-white/10 text-white" data-testid="period-filter">
                          <SelectValue placeholder="All Time" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a2e] border-white/10">
                          <SelectItem value="all">All Time</SelectItem>
                          <SelectItem value="24h">Last 24 Hours</SelectItem>
                          <SelectItem value="1week">Last Week</SelectItem>
                          <SelectItem value="1month">Last Month</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <ScrollArea className="h-[400px]">
                    {historyLoading ? (
                      <div className="flex items-center justify-center py-20">
                        <div className="w-10 h-10 rounded-full border-4 border-[#9945FF]/30 border-t-[#9945FF] animate-spin" />
                      </div>
                    ) : paginatedHistory.length === 0 ? (
                      <div className="text-center py-16 text-gray-500">
                        <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No transactions found</p>
                        <p className="text-sm">Try a different time period</p>
                      </div>
                    ) : (
                      <div className="p-4 space-y-2">
                        {paginatedHistory.map((txn, idx) => (
                          <motion.div
                            key={txn.id || idx}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.03 }}
                            className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-transparent hover:border-white/10"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`p-2.5 rounded-xl ${txn.type === 'in' ? 'bg-[#00FF88]/10 border border-[#00FF88]/30' : 'bg-[#FF3366]/10 border border-[#FF3366]/30'}`}>
                                  {txn.type === 'in' 
                                    ? <ArrowUpRight className="w-5 h-5 text-[#00FF88]" />
                                    : <ArrowDownRight className="w-5 h-5 text-[#FF3366]" />
                                  }
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-bold text-white">
                                      {txn.type === 'in' ? 'DEPOSIT' : 'WITHDRAWAL'}
                                    </p>
                                    {txn.player_name && (
                                      <Badge className="bg-white/10 text-gray-300 border-0 text-xs">
                                        <Users className="w-3 h-3 mr-1" /> {txn.player_name}
                                      </Badge>
                                    )}
                                  </div>
                                  {txn.notes && <p className="text-sm text-gray-400 mt-0.5">{txn.notes}</p>}
                                  <p className="text-xs text-gray-500 mt-1">By {txn.created_by_name || 'System'}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className={`text-xl font-mono font-bold ${txn.type === 'in' ? 'text-[#00FF88]' : 'text-[#FF3366]'}`}>
                                  {txn.type === 'in' ? '+' : '-'}{formatCurrency(txn.amount)}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">{formatDateTime(txn.created_at)}</p>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                  
                  {/* Pagination */}
                  {totalHistoryPages > 1 && (
                    <div className="p-4 border-t border-white/5 flex items-center justify-between">
                      <p className="text-sm text-gray-400">
                        Page {historyPage} of {totalHistoryPages} ({walletHistory.length} transactions)
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={historyPage === 1}
                          onClick={() => setHistoryPage(p => p - 1)}
                          className="border-white/10"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={historyPage === totalHistoryPages}
                          onClick={() => setHistoryPage(p => p + 1)}
                          className="border-white/10"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              </>
            ) : (
              <Card className="bg-[#0d0d15] border-0 p-12 text-center">
                <Eye className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl font-bold text-white mb-2">Select a Wallet</h3>
                <p className="text-gray-400">Click on a wallet card to view its details and transaction history</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Add/Edit Wallet Modal */}
      <AnimatePresence>
        {showAddWallet && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowAddWallet(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md"
            >
              <Card className="bg-[#0d0d15] border border-[#00FF88]/30 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00FF88] to-transparent" />
                
                <div className="p-4 border-b border-white/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-[#00FF88]/20">
                        {editingWallet ? <Edit2 className="w-6 h-6 text-[#00FF88]" /> : <Plus className="w-6 h-6 text-[#00FF88]" />}
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white">{editingWallet ? 'EDIT' : 'ADD'} WALLET</h2>
                        <p className="text-sm text-gray-400">{editingWallet ? 'Update wallet settings' : 'Create a new payment wallet'}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setShowAddWallet(false)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                <form onSubmit={handleCreateWallet} className="p-4 space-y-4">
                  <div>
                    <Label className="text-gray-300">Wallet Name *</Label>
                    <Input
                      value={walletForm.wallet_name}
                      onChange={(e) => setWalletForm({ ...walletForm, wallet_name: e.target.value })}
                      placeholder="e.g., Cash App, Venmo, Chime..."
                      className="bg-[#1a1a2e] border-white/10 text-white mt-1"
                      required
                      data-testid="wallet-name-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">The payment platform or bank name</p>
                  </div>
                  
                  <div>
                    <Label className="text-gray-300">Tag Name (Optional)</Label>
                    <Input
                      value={walletForm.tag_name}
                      onChange={(e) => setWalletForm({ ...walletForm, tag_name: e.target.value })}
                      placeholder="e.g., katleen, john_doe..."
                      className="bg-[#1a1a2e] border-white/10 text-white mt-1"
                      data-testid="wallet-tag-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Username or identifier for this account</p>
                  </div>
                  
                  <div>
                    <Label className="text-gray-300">Receive Limit ($)</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={walletForm.receive_limit}
                        onChange={(e) => setWalletForm({ ...walletForm, receive_limit: e.target.value })}
                        placeholder="0.00 (no limit)"
                        className="pl-9 bg-[#1a1a2e] border-white/10 text-white font-mono"
                        data-testid="wallet-limit-input"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Warning will appear when balance reaches this limit. Leave empty for no limit.</p>
                  </div>

                  {/* Auto Approve Toggle */}
                  <div className="p-3 rounded-xl border" style={{
                    background: walletForm.auto_approve ? 'rgba(0,255,136,0.08)' : 'rgba(255,255,255,0.02)',
                    borderColor: walletForm.auto_approve ? 'rgba(0,255,136,0.3)' : 'rgba(255,255,255,0.1)'
                  }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Zap className="w-5 h-5" style={{ color: walletForm.auto_approve ? '#00FF88' : '#666' }} />
                        <div>
                          <p className="font-bold text-white text-sm">Auto Approve</p>
                          <p className="text-xs text-gray-400">Deposits using this wallet get approved instantly</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setWalletForm({ ...walletForm, auto_approve: !walletForm.auto_approve })}
                        className="relative w-12 h-6 rounded-full transition-colors"
                        style={{ background: walletForm.auto_approve ? '#00FF88' : '#333' }}
                        data-testid="auto-approve-toggle"
                      >
                        <motion.div
                          animate={{ x: walletForm.auto_approve ? 24 : 2 }}
                          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                          className="absolute top-1 w-4 h-4 rounded-full bg-white shadow"
                        />
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button 
                      type="submit" 
                      className="flex-1 bg-gradient-to-r from-[#00FF88] to-[#00F5FF] text-black font-bold"
                      data-testid="save-wallet-btn"
                    >
                      <Check className="w-4 h-4 mr-2" /> {editingWallet ? 'UPDATE' : 'CREATE'} WALLET
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowAddWallet(false)} className="flex-1 border-white/10">
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cash to Player Modal */}
      <AnimatePresence>
        {showCashToPlayer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowCashToPlayer(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md"
            >
              <Card className="bg-[#0d0d15] border border-[#FF3366]/30 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF3366] to-transparent" />
                
                <div className="p-4 border-b border-white/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-[#FF3366]/20">
                        <Send className="w-6 h-6 text-[#FF3366]" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white">CASH TO PLAYER</h2>
                        <p className="text-sm text-gray-400">Dispatch funds from a wallet to a player</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setShowCashToPlayer(false)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                <form onSubmit={handleCashToPlayer} className="p-4 space-y-4">
                  {/* Wallet Selection */}
                  <div>
                    <Label className="text-gray-300">From Wallet *</Label>
                    <Select 
                      value={cashToPlayerForm.wallet_id} 
                      onValueChange={(v) => setCashToPlayerForm({ ...cashToPlayerForm, wallet_id: v })}
                    >
                      <SelectTrigger className="w-full bg-[#1a1a2e] border-white/10 text-white mt-1" data-testid="cash-wallet-select">
                        <SelectValue placeholder="Select wallet..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a2e] border-white/10">
                        {wallets.map(w => (
                          <SelectItem key={w.id} value={w.id}>
                            <div className="flex items-center gap-2">
                              <span>{w.wallet_name}</span>
                              {w.tag_name && <span className="text-gray-400">- {w.tag_name}</span>}
                              <span className="text-[#FFD700] font-mono ml-2">{formatCurrency(w.balance || 0)}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Player Selection */}
                  <div>
                    <Label className="text-gray-300">Player *</Label>
                    <div className="relative mt-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        value={cashToPlayerForm.player_name}
                        onChange={(e) => { 
                          setCashToPlayerForm({ ...cashToPlayerForm, player_name: e.target.value, player_id: '' });
                          setPlayerSearch(e.target.value);
                        }}
                        placeholder="Search or type player name..."
                        className="pl-9 bg-[#1a1a2e] border-white/10 text-white"
                        required
                        data-testid="cash-player-input"
                      />
                    </div>
                    {/* Player suggestions */}
                    {playerSearch && filteredPlayers.length > 0 && (
                      <div className="mt-2 rounded-lg bg-[#1a1a2e] border border-white/10 max-h-40 overflow-y-auto">
                        {filteredPlayers.map(p => (
                          <button
                            key={p.id}
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 flex items-center justify-between"
                            onClick={() => {
                              setCashToPlayerForm({ ...cashToPlayerForm, player_name: p.name, player_id: p.id });
                              setPlayerSearch('');
                            }}
                          >
                            <span>{p.name}</span>
                            <span className="text-gray-400 text-xs">{p.phone || p.email || ''}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Amount */}
                  <div>
                    <Label className="text-gray-300">Amount *</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={cashToPlayerForm.amount}
                        onChange={(e) => setCashToPlayerForm({ ...cashToPlayerForm, amount: e.target.value })}
                        placeholder="0.00"
                        className="pl-9 bg-[#1a1a2e] border-white/10 text-white text-lg font-mono"
                        required
                        data-testid="cash-amount-input"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button 
                      type="submit" 
                      className="flex-1 bg-gradient-to-r from-[#FF3366] to-[#FF6B00] text-white font-bold"
                      disabled={!cashToPlayerForm.wallet_id || !cashToPlayerForm.player_name || !cashToPlayerForm.amount}
                      data-testid="send-cash-btn"
                    >
                      <Send className="w-4 h-4 mr-2" /> SEND CASH
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowCashToPlayer(false)} className="flex-1 border-white/10">
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm"
            >
              <Card className="bg-[#0d0d15] border border-[#FF3366]/30 p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF3366]/20 flex items-center justify-center">
                  <Trash2 className="w-8 h-8 text-[#FF3366]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Delete Wallet?</h3>
                <p className="text-gray-400 text-sm mb-6">This action cannot be undone. The wallet and its transaction history will be permanently deleted.</p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-white/10"
                    onClick={() => setDeleteConfirm(null)}
                    data-testid="cancel-delete-wallet"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-[#FF3366] hover:bg-[#FF3366]/80 text-white"
                    onClick={confirmDeleteWallet}
                    data-testid="confirm-delete-wallet"
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
